import { Wallet } from 'lucide-preact';
import { useEffect, useState } from 'preact/hooks';
import { isDesktop } from 'react-device-detect';

import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { useNavigation } from '@/context/NavigationContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { getWalletDeepLink } from '@/helpers/functions/wallet';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import type { I_LoyaltyCardProps } from '@/types/RegisteredCustomer';
import { E_AppRoutes } from '@/types/Routes';

import { BottomSheet } from './BottomSheet';
import { Spinner } from './Spinner';
import { ScanQR } from './Wallet/ScanQR';
import WalletButtons from './Wallet/WalletButtons';

export const LoyaltyCard = ({ loading }: I_LoyaltyCardProps) => {
  const { navigateTo } = useNavigation();
  const { loyaltyCardStatus, qrCodeUrl, customerHasAddedLoyaltyCard, setBrandData, brandData } =
    useLoyaltyProgramContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isBrandDataLoading, setIsBrandDataLoading] = useState(false);
  const { getShopLoyaltyConfig } = usePlatformHandlers();

  const { theme } = useWidgetConfig();

  const {
    backgroundColor: primaryButtonBackgroundColor = COLORS.BRAND_PRIMARY,
    textColor: primaryButtonTextColor = COLORS.TEXT_INVERSE,
  } = theme?.primaryButton ?? {};

  const {
    backgroundColor: primaryButtonHoverBackground,
    hoverHandlers: primaryButtonHoverHandlers,
  } = useHoverBackground({
    baseColor: primaryButtonBackgroundColor,
  });

  const fetchBrandData = async (isMountedRef: { current: boolean }) => {
    setIsBrandDataLoading(true);
    try {
      const data = await getShopLoyaltyConfig?.();
      if (!isMountedRef.current) return;

      if (data?.brands && data?.brands?.length > 0) {
        setBrandData(data.brands[0]);
      }
    } catch (err) {
      console.error('Error fetching brand data:', err);
    } finally {
      if (isMountedRef.current) {
        setIsBrandDataLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!brandData && loyaltyCardStatus && !customerHasAddedLoyaltyCard(loyaltyCardStatus)) {
      const isMountedRef = { current: true };

      void fetchBrandData(isMountedRef);

      return () => {
        isMountedRef.current = false;
      };
    }
  }, [brandData, loyaltyCardStatus]);

  return (
    <>
      {loyaltyCardStatus && !customerHasAddedLoyaltyCard(loyaltyCardStatus) ? (
        <div className='mt-6 space-y-3'>{isBrandDataLoading ? <Spinner /> : <WalletButtons />}</div>
      ) : loyaltyCardStatus ? (
        <div className='mt-4 rounded-lg p-4'>
          {!isDesktop ? (
            <a
              {...primaryButtonHoverHandlers}
              href={getWalletDeepLink(loyaltyCardStatus, qrCodeUrl) || '#'}
              className='flex w-full items-center justify-center space-x-2 rounded bg-transparent py-2 no-underline transition-colors'
              style={{
                backgroundColor: primaryButtonHoverBackground,
              }}
            >
              <Wallet className='h-5 w-5' style={{ color: primaryButtonTextColor }} />
              <span className='font-medium' style={{ color: primaryButtonTextColor }}>
                Open Loyalty Card
              </span>
            </a>
          ) : (
            <button
              className='flex w-full items-center justify-center space-x-2 rounded bg-transparent py-2 transition-colors'
              style={{
                backgroundColor: primaryButtonHoverBackground,
                color: primaryButtonTextColor,
              }}
              onClick={() => setIsOpen(true)}
              {...primaryButtonHoverHandlers}
            >
              <Wallet className='h-5 w-5' />
              <span className='font-medium' style={{ color: primaryButtonTextColor }}>
                Open Loyalty Card
              </span>
            </button>
          )}

          <div className='mt-3 border-t bg-transparent pt-3'>
            <button
              {...primaryButtonHoverHandlers}
              disabled={isBrandDataLoading}
              className={`bg-transparent text-sm transition-colors ${
                isBrandDataLoading ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{
                backgroundColor: isBrandDataLoading
                  ? COLORS.DISABLED_LIGHT
                  : primaryButtonHoverBackground,
                color: isBrandDataLoading ? COLORS.DISABLED_DARK : primaryButtonTextColor,
              }}
              onClick={async () => {
                if (isBrandDataLoading) return;
                await fetchBrandData({ current: true });
                navigateTo(E_AppRoutes.POST_REGISTRATION);
              }}
            >
              {isBrandDataLoading ? 'Loading...' : 'Re-add to Wallet'}
            </button>
          </div>
        </div>
      ) : null}

      <div className='bottom-3'>
        <BottomSheet
          disclosure={{
            isOpen,
            onClose: () => setIsOpen(false),
          }}
        >
          {loading ? <Spinner /> : <ScanQR />}
        </BottomSheet>
      </div>
    </>
  );
};
